import { useState } from 'react';
import { TableCellComponentProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Visual } from '@/modules/Visual/Index';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { Lightbox } from '@/modules/Lightbox';
import { ILightboxImage, ImageDisplayMode } from '@/modules/Lightbox/types/lightbox.types';
import { VisualSlotType, IconFontColor } from '@/shared/types/types';

interface IconCellConfig {
  icon?: VisualSlotType;
  color?: IconFontColor;
  images?: string | string[] | ILightboxImage | ILightboxImage[];
  displayMode?: ImageDisplayMode;
  className?: string;
}

export const IconCell = ({
  value,
  row,
  column,
  state = 'default',
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    undefined,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);
  const alignClass = getContentAlignClass(column.contentAlign);

  const iconConfig: IconCellConfig | null =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as IconCellConfig)
      : value !== null && value !== undefined
        ? { icon: value as VisualSlotType }
        : null;

  // Detectar imágenes: puede ser un array de imágenes, una URL string, o un objeto con imágenes
  // Solo se usa la primera imagen para el lightbox
  let images: ILightboxImage[] | undefined;

  // Función helper para convertir una URL string a ILightboxImage
  const stringToImage = (url: string): ILightboxImage => ({
    id: 0,
    src: url,
    title: undefined,
  });

  if (iconConfig?.images) {
    if (Array.isArray(iconConfig.images) && iconConfig.images.length > 0) {
      // Array de imágenes: usar la primera
      const singleImage = iconConfig.images[0];
      if (singleImage) {
        if (typeof singleImage === 'string') {
          images = [stringToImage(singleImage)];
        } else {
          images = [singleImage];
        }
      }
    } else if (typeof iconConfig.images === 'string') {
      // URL string: convertir a formato ILightboxImage
      images = [stringToImage(iconConfig.images)];
    } else if (typeof iconConfig.images === 'object' && 'src' in iconConfig.images) {
      // Objeto ILightboxImage
      images = [iconConfig.images];
    }
  } else if (value) {
    if (Array.isArray(value) && value.length > 0) {
      if (value[0]?.src) {
        // Array de objetos ILightboxImage: usar la primera
        const singleImage = value[0] as ILightboxImage;
        if (singleImage) {
          images = [singleImage];
        }
      } else if (typeof value[0] === 'string') {
        // Array de strings: usar el primero
        images = [stringToImage(value[0])];
      }
    } else if (typeof value === 'string') {
      // URL string directa
      images = [stringToImage(value)];
    }
  } else if (row && typeof row === 'object' && row !== null && 'images' in row) {
    const rowImages = (row as { images?: unknown }).images;
    if (Array.isArray(rowImages) && rowImages.length > 0) {
      if (typeof rowImages[0] === 'string') {
        // Array de strings: usar el primero
        images = [stringToImage(rowImages[0])];
      } else if (rowImages[0]?.src) {
        // Array de objetos: usar el primero
        const singleImage = rowImages[0];
        if (singleImage) {
          images = [singleImage];
        }
      }
    } else if (typeof rowImages === 'string') {
      // URL string directa
      images = [stringToImage(rowImages)];
    }
  }

  const hasImages = images && images.length > 0;

  const handleIconClick = (e: React.MouseEvent) => {
    if (hasImages) {
      e.stopPropagation();
      setLightboxOpen(true);
    } else {
      handleCellClick(e, onCellFocus);
    }
  };

  const hasFixedWidth = column.width !== undefined;
  return (
    <td className={baseClasses} {...cellProps} onClick={handleIconClick}>
      <div
        className={cn(
          'ui-flex ui-h-full ui-items-center',
          alignClass,
          hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
          hasImages && 'ui-cursor-pointer',
        )}
      >
        <Visual
          slot={iconConfig?.icon}
          colorIcon={hasImages ? iconConfig?.color || 'tertiary' : iconConfig?.color || 'tertiary'}
          className={cn('ui-h-5 ui-w-5', iconConfig?.className)}
        />
      </div>
      {hasImages && images && (
        <Lightbox
          images={images}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          initialIndex={0}
          showTitle={true}
          showIndicators={images.length > 1}
          showNavigation={images.length > 1}
          showCounter={images.length > 1}
          displayMode={(iconConfig?.displayMode as ImageDisplayMode) || 'fullWidth'}
        />
      )}
    </td>
  );
};
